// 종목 시세 알림 설정 모달 (tb_stock_alert)
// 매수/매도 알림 설정 팝업(AlertQuickPopup)과 동일한 스타일(aqp-*)을 재사용한다.
import React, { useState, useEffect, useCallback } from 'react';
import { fetchAlertTypes } from '../../api/alert';
import { saveStockAlert, deleteStockAlert } from '../../api/stockAlert';
import { fetchRealtimePrice } from '../../api/price';
import './AlertQuickPopup.css';
import './StockAlertModal.css';

const StockAlertModal = ({ ticker, stockName, alertInfo, onClose, onSaved }) => {
  const [alertTypes,   setAlertTypes]   = useState([]);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [form,         setForm]         = useState({
    alert_type_id_up:   alertInfo?.alert_type_id_up   ?? null,
    alert_up_price:     alertInfo?.alert_up_price      ?? '',
    alert_type_id_down: alertInfo?.alert_type_id_down ?? null,
    alert_down_price:   alertInfo?.alert_down_price    ?? '',
    alert_enabled:      alertInfo?.alert_enabled       ?? true,
  });

  const hasRow = !!alertInfo?.id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [typesRes, priceRes] = await Promise.all([
        fetchAlertTypes(),
        fetchRealtimePrice(ticker).catch(() => null),
      ]);
      setAlertTypes(typesRes.data || []);
      if (priceRes) setCurrentPrice(priceRes.current_price ?? null);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [ticker]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveStockAlert({
        ticker,
        stock_name:         stockName,
        alert_type_id_up:   form.alert_type_id_up   || null,
        alert_up_price:     form.alert_up_price      === '' ? null : Number(form.alert_up_price),
        alert_type_id_down: form.alert_type_id_down || null,
        alert_down_price:   form.alert_down_price    === '' ? null : Number(form.alert_down_price),
        alert_enabled:      form.alert_enabled,
      });
      onSaved?.();
    } catch (e) {
      alert(e.response?.data?.detail || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('종목 알림 설정을 삭제하시겠습니까?')) return;
    setSaving(true);
    try {
      await deleteStockAlert(ticker);
      onSaved?.();
    } catch (e) {
      alert(e.response?.data?.detail || '삭제 실패');
    } finally {
      setSaving(false);
    }
  };

  // 상승 알림: UP 또는 BOTH 카테고리 유형
  const upTypes   = alertTypes.filter(t => t.is_active && ['UP', 'BOTH'].includes(t.alert_category));
  // 하락 알림: DOWN 또는 BOTH 카테고리 유형
  const downTypes = alertTypes.filter(t => t.is_active && ['DOWN', 'BOTH'].includes(t.alert_category));

  return (
    <div className="aqp-overlay" onClick={onClose}>
      <div className="aqp-popup" onClick={e => e.stopPropagation()}>
        <div className="aqp-hd">
          🔔 종목 시세 알림 설정
          <button className="aqp-close" onClick={onClose}>✕</button>
        </div>
        <div className="aqp-sub">
          {stockName || ticker}
          <span className="aqp-acct">({ticker})</span>
        </div>

        {loading ? (
          <div className="aqp-loading">조회중...</div>
        ) : (
          <div className="aqp-bd">
            {/* 알림 활성 토글 */}
            <div className="aqp-enabled-row">
              <label className="aqp-enabled-label">
                <input
                  type="checkbox"
                  checked={form.alert_enabled}
                  onChange={e => setForm(p => ({ ...p, alert_enabled: e.target.checked }))}
                />
                <span>알림 활성</span>
              </label>
            </div>

            {/* 상승 알림 */}
            <div className="aqp-row">
              <span className="aqp-row-label aqp-up">상승 알림</span>
              <select
                className="aqp-select"
                value={form.alert_type_id_up ?? ''}
                onChange={e => setForm(p => ({
                  ...p,
                  alert_type_id_up: e.target.value ? parseInt(e.target.value) : null,
                }))}
              >
                <option value="">선택안함</option>
                {upTypes.map(t => <option key={t.id} value={t.id}>{t.type_name}</option>)}
              </select>
            </div>
            <div className="aqp-row">
              <span className="aqp-row-label">목표가</span>
              <input
                className="sam-price-input"
                type="number" min="0" step="1"
                value={form.alert_up_price}
                onChange={e => setForm(p => ({ ...p, alert_up_price: e.target.value }))}
                placeholder="예) 30000"
              />
              <span className="sam-price-suffix">원</span>
            </div>

            {/* 하락 알림 */}
            <div className="aqp-row">
              <span className="aqp-row-label aqp-down">하락 알림</span>
              <select
                className="aqp-select"
                value={form.alert_type_id_down ?? ''}
                onChange={e => setForm(p => ({
                  ...p,
                  alert_type_id_down: e.target.value ? parseInt(e.target.value) : null,
                }))}
              >
                <option value="">선택안함</option>
                {downTypes.map(t => <option key={t.id} value={t.id}>{t.type_name}</option>)}
              </select>
            </div>
            <div className="aqp-row">
              <span className="aqp-row-label">목표가</span>
              <input
                className="sam-price-input"
                type="number" min="0" step="1"
                value={form.alert_down_price}
                onChange={e => setForm(p => ({ ...p, alert_down_price: e.target.value }))}
                placeholder="예) 25000"
              />
              <span className="sam-price-suffix">원</span>
            </div>

            {alertTypes.length === 0 && (
              <div className="aqp-hint">
                알림 유형이 없습니다. 다른 알림 설정 화면에서 먼저 유형을 만드세요.
              </div>
            )}

            <div className="sam-current-price">
              현재가 <strong>{currentPrice != null ? `${Number(currentPrice).toLocaleString()}원` : '-'}</strong>
              <span className="sam-current-price-note">(목표가 설정 참고용)</span>
            </div>
          </div>
        )}

        <div className="aqp-ft">
          {hasRow
            ? <button className="btn btn-sm btn-danger" onClick={handleDelete} disabled={saving || loading}>삭제</button>
            : <span />}
          <div className="aqp-ft-right">
            <button className="btn btn-sm btn-outline" onClick={onClose}>닫기</button>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving ? '저장중...' : '저장'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockAlertModal;
